import { normalisePlate } from './kentekenMatcher.js'
import { planPayload, portablePlanInputs, SPECIALIST_CHOICES } from './planDrafts.js'
import { SCENARIO_FIELDS } from './specialistScenario.js'

// Optional device-local form drafts, never stored valuations or RDW evidence.
export const KENTEKEN_PREFIX = 'automargin_kenteken_draft_v1:'
export const KENTEKEN_EVENT = 'automargin-kenteken-drafts-changed'
export const MAX_KENTEKEN_DRAFTS = 20
export const validDraftMileage = value => typeof value === 'string' &&
  (value === '' || (/^\d{1,7}$/.test(value) && Number(value) <= 1500000))
const keyFor = plate => KENTEKEN_PREFIX + plate
const validDate = value => typeof value === 'string' && /^\d{4}-\d\d-\d\dT.*Z$/.test(value) && Number.isFinite(Date.parse(value))

function specialistInputs(value) {
  if (!value || SCENARIO_FIELDS.some(key => typeof value.inputs?.[key] !== 'string' || value.inputs[key].length > 32) ||
      Object.entries(SPECIALIST_CHOICES).some(([key, allowed]) => !allowed.includes(value.choices?.[key]))) throw Error('invalid')
  return planPayload('specialist', value)
}

function specialistDraft(value) {
  if (!value || !validDraftMileage(value.mileage) || value.mileage === '' || !validDate(value.saved_at)) throw Error('invalid')
  return { mileage: value.mileage, saved_at: value.saved_at, payload: specialistInputs(value.payload) }
}

// A plate is not a foreign listing or an inspected car. Always require explicit
// reuse after fresh lookup; never restore a price/import quote or evidence choice.
export const reusablePlateScenario = specialist => portablePlanInputs({ payload: specialist.payload }, null, 'specialist')

function validDraft(value, key) {
  if (!value || !['automargin-kenteken-draft.v1', 'automargin-kenteken-draft.v2'].includes(value.schema) ||
      (value.schema === 'automargin-kenteken-draft.v2' && value.specialist === undefined) ||
      typeof value.plate !== 'string' || !/^[A-Z0-9]{6}$/.test(value.plate) || key !== keyFor(value.plate) ||
      !validDraftMileage(value.mileage) || typeof value.revision !== 'string' ||
      !value.revision || value.revision.length > 100 || typeof value.saved_at !== 'string' ||
      !/^\d{4}-\d\d-\d\dT.*Z$/.test(value.saved_at) || !Number.isFinite(Date.parse(value.saved_at))) throw Error('invalid')
  return { schema: value.schema, plate: value.plate, mileage: value.mileage,
    revision: value.revision, saved_at: value.saved_at,
    ...(value.specialist === undefined ? {} : { specialist: specialistDraft(value.specialist) }) }
}

export function readKentekenDraft(storage, plate) {
  if (!storage) return { draft: null, error: 'unavailable' }
  try {
    const key = keyFor(plate), raw = storage.getItem(key)
    if (raw === null) return { draft: null, error: null }
    try {
      if (raw.length > 2000) throw Error('invalid')
      return { draft: validDraft(JSON.parse(raw), key), error: null }
    } catch { return { draft: null, error: 'invalid' } }
  } catch { return { draft: null, error: 'unavailable' } }
}

export function listKentekenDrafts(storage) {
  if (!storage) return { drafts: [], error: 'unavailable' }
  try {
    const drafts = []
    let error = null
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)
      if (!key?.startsWith(KENTEKEN_PREFIX)) continue
      const read = readKentekenDraft(storage, key.slice(KENTEKEN_PREFIX.length))
      if (read.error) error = read.error
      else if (read.draft) drafts.push(read.draft)
    }
    return { drafts: drafts.sort((a, b) => b.saved_at.localeCompare(a.saved_at)), error }
  } catch { return { drafts: [], error: 'unavailable' } }
}

export function writeKentekenDraft(storage, { plate, mileage, specialist, expectedRevision = null, now = new Date() }) {
  const normalized = normalisePlate(plate)
  if (!normalized || !validDraftMileage(mileage)) return { draft: null, error: 'input' }
  const previous = readKentekenDraft(storage, normalized)
  if (previous.error) return previous
  if ((previous.draft?.revision ?? null) !== expectedRevision) return { draft: null, error: 'conflict' }
  const listed = listKentekenDrafts(storage)
  if (listed.error) return { draft: null, error: listed.error }
  if (!previous.draft && listed.drafts.length >= MAX_KENTEKEN_DRAFTS) return { draft: null, error: 'limit' }
  let scenario = previous.draft?.specialist
  try { if (specialist !== undefined) scenario = specialistDraft(specialist) }
  catch { return { draft: null, error: 'input' } }
  try {
    // Old open clients only understand v1. They must refuse a scenario-bearing
    // record, not silently strip the new fields when saving mileage.
    const draft = { schema: scenario ? 'automargin-kenteken-draft.v2' : 'automargin-kenteken-draft.v1', plate: normalized, mileage,
      saved_at: now.toISOString(), revision: `${now.toISOString()}:${Math.random().toString(36).slice(2)}`,
      ...(scenario ? { specialist: scenario } : {}) }
    if (JSON.stringify(draft).length > 2000) return { draft: null, error: 'input' }
    storage.setItem(keyFor(normalized), JSON.stringify(draft))
    return { draft, error: null }
  } catch { return { draft: null, error: 'unavailable' } }
}

export function writePlateScenario(storage, { plate, mileage, payload, expectedRevision = null, now = new Date() }) {
  if (!validDraftMileage(mileage) || mileage === '') return { draft: null, error: 'input' }
  const previous = readKentekenDraft(storage, normalisePlate(plate))
  if (previous.error) return previous
  // Editing a budget for the displayed old odometer must not undo a separately
  // saved new dashboard reading. The scenario carries its own odometer binding.
  return writeKentekenDraft(storage, { plate, mileage: previous.draft?.mileage ?? mileage, expectedRevision, now,
    specialist: { mileage, payload, saved_at: now.toISOString() } })
}

export function removeKentekenDraft(storage, draft) {
  const current = readKentekenDraft(storage, draft?.plate)
  if (current.error) return current.error
  if (!current.draft || current.draft.revision !== draft?.revision) return 'conflict'
  try { storage.removeItem(keyFor(draft.plate)); return null } catch { return 'unavailable' }
}
